var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import { stringify } from 'query-string';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import uuid from 'uuid';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { PROFFIT_SEARCH_SEARCH_IS_SHOW_SIMILAR_SLIDES, PROFFIT_SEARCH_SEARCH_SORTS, PROFFIT_SEARCH_SEARCH_UNITS, } from '../../../../define/consts/proffit_search/search';
import { wrapDispatch } from '../../../action';
import { parseQueryString } from '../../../common/QueryStringHelper';
import { useBoolState } from '../../useHook';
/**
 * 検索メニュー左側のアコーディオン開閉を制御するuse
 * @returns {*}
 */
const useSlideSearchListAccordion = () => {
    // フィルターサイドメニュー表示の制御
    const { boolState: isShowFilter, changeToggle: toggleIsShowFilter } = useBoolState(true);
    return {
        isShowFilter,
        toggleIsShowFilter,
    };
};
/**
 * 検索フォームの類似スライド更新時のイベント
 * @param getReplaceUrl
 * @returns {(function(*): void)|*}
 */
const useSlideSearchOnChangeLikenessSlide = (getReplaceUrl) => {
    const history = useNavigate();
    return useCallback((e) => {
        const { value } = e.currentTarget;
        const { url } = getReplaceUrl((v) => (Object.assign(Object.assign({}, v), { isShowLikenessSlide: value, page: 1 })));
        // 初期状態に戻す
        history.push(url, { state: undefined });
    }, [history, getReplaceUrl]);
};
/**
 * 検索フォームのキーワード更新（blur)時のイベント
 * @param getReplaceUrl
 * @param keywordRef
 * @returns {(function(): void)|*}
 */
const useSlideSearchOnUpdateKeyword = (getReplaceUrl, keywordRef) => {
    const history = useNavigate();
    return useCallback(() => {
        const { defaultValue, value } = keywordRef.current;
        if (defaultValue === value) {
            return;
        }
        const { url } = getReplaceUrl((v) => (Object.assign(Object.assign({}, v), { keyword: value, page: 1 })));
        history.push(url, { state: undefined });
    }, [history, getReplaceUrl, keywordRef]);
};
/**
 * 検索画面でのスライド種別の変更
 * @param getReplaceUrl
 * @param slideTypes
 * @returns {(function(*): void)|*}
 */
const useSlideSearchOnChangeSlideType = (getReplaceUrl, slideTypes) => {
    const history = useNavigate();
    return useCallback((e) => {
        const value = parseInt(e.currentTarget.value, 10);
        const prevSlideTypes = slideTypes
            .split(',')
            .filter((v) => v)
            .map((v) => parseInt(v, 10));
        const newSlideTypes = _.xor(prevSlideTypes, [value]).join(',');
        const { url } = getReplaceUrl((v) => (Object.assign(Object.assign({}, v), { page: 1, slideTypes: newSlideTypes })));
        history.replace(url, { state: undefined });
    }, [history, getReplaceUrl, slideTypes]);
};
/**
 * 検索画面でのselect2の変更
 * @param getReplaceUrl
 * @param key
 * @param value
 * @returns {(function(*): void)|*}
 */
const useSlideSearchOnChangeSelect2 = (getReplaceUrl, key, value) => {
    const history = useNavigate();
    return useCallback((option) => {
        if (option.value === value.value) {
            return;
        }
        const { url } = getReplaceUrl((v) => (Object.assign(Object.assign({}, v), { [key]: option.value, page: 1 })));
        history.push(url, { state: undefined });
    }, [history, getReplaceUrl, key, value]);
};
/**
 * ページの遷移
 * @param getReplaceUrl
 * @returns {(function({selected: *}): void)|*}
 */
const useSlideSearchOnChangePage = (getReplaceUrl) => {
    const history = useNavigate();
    return useCallback(({ selected }) => {
        const value = selected + 1;
        const { url } = getReplaceUrl((v) => (Object.assign(Object.assign({}, v), { page: value })));
        history.push(url, { state: undefined });
    }, [history, getReplaceUrl]);
};
/**
 * 検索画面でのパラメータの取得
 * @param location
 * @returns {{query: (any), isShowLikenessSlide: *, onChangeSort: *, sort, keywordRef: React.MutableRefObject<undefined>, onChangeSlideType: *, slideTypes, unit, onChangePage: ((function({selected: *}): void)|*), onUpdateKeyword: *, onChangeUnit: *, formatQuery: {}, page, keyword, onChangeLikenessSlide: ((function(*): void)|*)}}
 */
const useSlideSearchListSearchParameter = (location) => {
    const { pathname, search: locationSearch } = location;
    const { formatQuery, query } = useMemo(() => parseQueryString(locationSearch, [
        {
            defaultIndex: 1,
            name: 'isShowLikenessSlide',
            selects: PROFFIT_SEARCH_SEARCH_IS_SHOW_SIMILAR_SLIDES.map((v) => v.value),
        },
        { name: 'keyword', type: 'string' },
        { name: 'slideTypes', type: 'string' },
        { name: 'sort', options: PROFFIT_SEARCH_SEARCH_SORTS },
        { name: 'unit', options: PROFFIT_SEARCH_SEARCH_UNITS },
        { name: 'page', type: 'integer' },
    ]), [locationSearch]);
    const keywordRef = useRef();
    const getReplaceUrl = useCallback((updateCallback = null) => {
        const parseParam = updateCallback ? updateCallback(query) : query;
        const rawSearchQueryStr = stringify(parseParam);
        const searchQueryStr = rawSearchQueryStr
            ? `?${rawSearchQueryStr}`
            : rawSearchQueryStr;
        return {
            param: parseParam,
            url: `${pathname}${searchQueryStr}`,
        };
    }, [query, pathname]);
    const onUpdateKeyword = useSlideSearchOnUpdateKeyword(getReplaceUrl, keywordRef);
    const onChangeLikenessSlide = useSlideSearchOnChangeLikenessSlide(getReplaceUrl);
    const onChangeSlideType = useSlideSearchOnChangeSlideType(getReplaceUrl, formatQuery.slideTypes);
    const onChangeSort = useSlideSearchOnChangeSelect2(getReplaceUrl, 'sort', formatQuery.sort);
    const onChangeUnit = useSlideSearchOnChangeSelect2(getReplaceUrl, 'unit', formatQuery.unit);
    const onChangePage = useSlideSearchOnChangePage(getReplaceUrl);
    return {
        formatQuery,
        isShowLikenessSlide: formatQuery.isShowLikenessSlide,
        keyword: formatQuery.keyword,
        keywordRef,
        onChangeLikenessSlide,
        onChangePage,
        onChangeSlideType,
        onChangeSort,
        onChangeUnit,
        onUpdateKeyword,
        page: formatQuery.page,
        query,
        slideTypes: formatQuery.slideTypes,
        sort: formatQuery.sort,
        unit: formatQuery.unit,
    };
};
/**
 * 各詳細画面でのパラメータの取得
 * @param location
 * @returns {{onChangeSlideType: *, slideTypes, unit, onChangePage: ((function({selected: *}): void)|*), query: (any), onUpdateKeyword: *, onChangeUnit: *, formatQuery: {}, page, keyword, keywordRef: React.MutableRefObject<undefined>}}
 */
const useSlideSearchListSearchOneParameter = (location) => {
    const { pathname, search: locationSearch } = location;
    const { formatQuery, query } = useMemo(() => parseQueryString(locationSearch, [
        { name: 'keyword', type: 'string' },
        { name: 'slideTypes', type: 'string' },
        { name: 'unit', options: PROFFIT_SEARCH_SEARCH_UNITS },
        { name: 'page', type: 'integer' },
    ]), [locationSearch]);
    const keywordRef = useRef();
    const getReplaceUrl = useCallback((updateCallback = null) => {
        const parseParam = updateCallback ? updateCallback(query) : query;
        const rawSearchQueryStr = stringify(parseParam);
        const searchQueryStr = rawSearchQueryStr
            ? `?${rawSearchQueryStr}`
            : rawSearchQueryStr;
        return {
            param: parseParam,
            url: `${pathname}${searchQueryStr}`,
        };
    }, [query, pathname]);
    const onUpdateKeyword = useSlideSearchOnUpdateKeyword(getReplaceUrl, keywordRef);
    const onChangeSlideType = useSlideSearchOnChangeSlideType(getReplaceUrl, formatQuery.slideTypes);
    const onChangeUnit = useSlideSearchOnChangeSelect2(getReplaceUrl, 'unit', formatQuery.unit);
    const onChangePage = useSlideSearchOnChangePage(getReplaceUrl);
    return {
        formatQuery,
        keyword: formatQuery.keyword,
        keywordRef,
        onChangePage,
        onChangeSlideType,
        onChangeUnit,
        onUpdateKeyword,
        page: formatQuery.page,
        query,
        slideTypes: formatQuery.slideTypes,
        unit: formatQuery.unit,
    };
};
/**
 * 検索画面でのAPIの実行
 * @param action
 * @param dispatch
 * @param unmountRef
 * @param location
 * @param formatQuery
 * @returns {{total, list, loading: boolean, aggs: *}}
 */
const useSlideSearchListSearchApi = (action, dispatch, unmountRef, location, formatQuery) => {
    const { pathname, search, state: locationState = {} } = location;
    const history = useNavigate();
    const didMountRef = useRef(false);
    const apiRef = useRef();
    const effectRef = useRef({});
    effectRef.current = {
        query: formatQuery,
        slideSearchData: locationState === null || locationState === void 0 ? void 0 : locationState.slideSearchData,
    };
    const initialPagingState = useMemo(() => ({
        aggs: {},
        list: [],
        total: 0,
    }), []);
    const [loading, changeLoading] = useState(false);
    // ページングの初期値の取得
    const [paging, changePaging] = useState((locationState === null || locationState === void 0 ? void 0 : locationState.slideSearchData) || initialPagingState);
    const performSearch = useCallback((callAction, updateUrl, params, initialPagingData) => __awaiter(void 0, void 0, void 0, function* () {
        const apiId = uuid();
        apiRef.current = apiId;
        changeLoading(true);
        changePaging(initialPagingData);
        const formatParams = {};
        Object.keys(params).forEach((key) => {
            const v = params[key];
            if (v) {
                formatParams[key] = typeof v === 'object' && v.value ? v.value : v;
            }
        });
        const res = yield wrapDispatch(dispatch, callAction(Object.assign(Object.assign({}, formatParams), { page: formatParams.page || 1 })));
        // 最後に実行されたAPIの結果で更新する
        if (apiRef.current !== apiId || unmountRef.current) {
            return null;
        }
        const { payload: { aggs, list, total: resTotal }, } = res;
        const slideSearchData = {
            aggs,
            list,
            total: resTotal,
        };
        changePaging(slideSearchData);
        history.replace(updateUrl, { state: { slideSearchData } });
        changeLoading(false);
        return res;
    }), [apiRef, changePaging, dispatch, history, unmountRef]);
    useEffect(() => {
        try {
            if (!didMountRef.current && effectRef.current.slideSearchData) {
                // 初回読み込み時にhistoryにデータがある場合はそれを使用する
                return;
            }
            performSearch(action, `${pathname}${search}`, effectRef.current.query, initialPagingState).then(null);
        }
        finally {
            didMountRef.current = true;
        }
    }, [
        didMountRef,
        effectRef,
        performSearch,
        action,
        pathname,
        search,
        initialPagingState,
    ]);
    return {
        aggs: paging.aggs,
        list: paging.list,
        loading: !(locationState === null || locationState === void 0 ? void 0 : locationState.slideSearchData) || loading,
        total: paging.total,
    };
};
export const useSlideSearchList = {
    useSlideSearchListAccordion,
    useSlideSearchListSearchApi,
    useSlideSearchListSearchOneParameter,
    useSlideSearchListSearchParameter,
};
export default useSlideSearchList;
